import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const WhyWeHoldEventSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [expandedSections, setExpandedSections] = useState([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'en';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'en';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const sectionContent = {
    ar: {
      title: "لماذا نحيي هذه الفعالية؟",
      sections: [
        {
          id: 'why-hold',
          title: 'لماذا نقيم هذه الفعالية؟',
          content: `يوم التحرير شكّل نقطة تحول لا يجوز مرورها دون إحياء أو توثيق.

الذكرى الأولى تمنح الحدث مكانه الطبيعي في الوعي العام.

الفعالية تفتح مساحة يجتمع فيها الناس لاستعادة تلك اللحظة بوضوحها ومعناها.`
        },
        {
          id: 'meaning-to-syrians',
          title: 'ماذا يعني هذا اليوم للسوريين؟',
          content: `انبعث شعور الانتماء لدى كثيرين بعد سنوات من البعد والتشتّت.

توحّدت نظرة الناس تجاه هذا اليوم رغم اختلاف تجاربهم وظروفهم.

برز شعور جديد لدى الجميع بأن الهوية ما زالت حيّة ويمكن البناء عليها.`
        },
        {
          id: 'why-pivotal',
          title: 'لماذا كان يوم التحرير مفصليًا؟',
          content: `تحوّلت صورة البلد في نظر الناس من الغموض إلى الوضوح.

انتهت مرحلة طويلة من الاضطراب وبدأت مرحلة إعادة الفهم وإعادة العلاقة مع الوطن.

تغيّر وعي السوريين تجاه مستقبلهم وإمكانات التغيير بعد تلك اللحظة.`
        },
        {
          id: 'goal',
          title: 'ما الهدف من هذه الفعالية؟',
          content: `تثبيت الذاكرة ونقل القصة كما عاشها أصحابها.

إعادة جمع السوريين حول رؤية مشتركة للمستقبل.

إبراز أن يوم التحرير كان بداية مسار يحتاج متابعة ووعيًا جماعيًا.`
        }
      ],
      closingStatement: "نلتقي لنحفظ الذاكرة، ونؤكد المعنى، ونمضي بخطوة واضحة نحو المستقبل.",
      expandText: 'اقرأ المزيد',
      collapseText: 'اقرأ أقل'
    },
    en: {
      title: "Why are we holding this event?",
      sections: [
        {
          id: 'why-hold',
          title: 'Why are we holding this event?',
          content: `Liberation Day marked a turning point that cannot pass without commemoration or documentation.

The first anniversary gives the event its natural place in public consciousness.

The event opens a space where people gather to reclaim that moment with its clarity and meaning.`
        },
        {
          id: 'meaning-to-syrians',
          title: 'What does this day mean to Syrians?',
          content: `A sense of belonging reawakened in many after years of distance and dispersion.

People's perspective on this day unified despite their different experiences and circumstances.

A new feeling emerged for everyone that identity is still alive and can be built upon.`
        },
        {
          id: 'why-pivotal',
          title: 'Why was Liberation Day pivotal?',
          content: `The country's image in people's eyes transformed from ambiguity to clarity.

A long phase of turmoil ended and a phase of re-understanding and re-establishing the relationship with the homeland began.

Syrians' consciousness about their future and possibilities for change transformed after that moment.`
        },
        {
          id: 'goal',
          title: 'What is the goal of this event?',
          content: `To preserve memory and transmit the story as those who lived it experienced it.

To reunite Syrians around a shared vision for the future.

To highlight that Liberation Day was the beginning of a path that requires follow-up and collective awareness.`
        }
      ],
      closingStatement: "We gather to preserve memory, affirm meaning, and take a clear step toward the future.",
      expandText: 'Read More',
      collapseText: 'Read Less'
    },
    tr: {
      title: "Bu etkinliği neden düzenliyoruz?",
      sections: [
        {
          id: 'why-hold',
          title: 'Bu etkinliği neden düzenliyoruz?',
          content: `Kurtuluş Günü, anma veya belgeleme olmadan geçemeyecek bir dönüm noktasıydı.

İlk yıldönümü, etkinliğe kamu bilincinde doğal yerini verir.

Etkinlik, insanların o anı netliği ve anlamıyla yeniden kazanmak için bir araya geldiği bir alan açar.`
        },
        {
          id: 'meaning-to-syrians',
          title: 'Bu gün Suriyeliler için ne anlama geliyor?',
          content: `Uzaklık ve dağılma yıllarından sonra birçok kişide aidiyet duygusu yeniden uyandı.

İnsanların bu güne bakış açıları, farklı deneyimlerine ve koşullarına rağmen birleşti.

Herkes için kimliğin hala canlı olduğu ve üzerine inşa edilebileceği yeni bir duygu ortaya çıktı.`
        },
        {
          id: 'why-pivotal',
          title: 'Kurtuluş Günü neden dönüm noktasıydı?',
          content: `Ülkenin insanların gözünde imajı belirsizlikten netliğe dönüştü.

Uzun bir kargaşa dönemi sona erdi ve vatanla ilişkiyi yeniden anlama ve yeniden kurma aşaması başladı.

O andan sonra Suriyelilerin gelecekleri ve değişim olasılıkları hakkındaki bilinçleri dönüştü.`
        },
        {
          id: 'goal',
          title: 'Bu etkinliğin amacı nedir?',
          content: `Belleği korumak ve hikayeyi yaşayanların deneyimlediği gibi aktarmak.

Suriyelileri gelecek için ortak bir vizyon etrafında yeniden bir araya getirmek.

Kurtuluş Günü'nün takip ve kolektif farkındalık gerektiren bir yolun başlangıcı olduğunu vurgulamak.`
        }
      ],
      closingStatement: "Belleği korumak, anlamı onaylamak ve geleceğe doğru net bir adım atmak için bir araya geliyoruz.",
      expandText: 'Devamını Oku',
      collapseText: 'Daha Az Oku'
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const content = sectionContent?.[currentLanguage];

  return (
    <section id="why-we-hold-event" className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            {content?.title}
          </h2>
        </motion.div>

        <div className="space-y-6">
          {content?.sections?.map((section, index) => (
            <motion.div
              key={section?.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-card border border-border rounded-lg overflow-hidden shadow-memorial"
            >
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full px-6 py-4 text-left memorial-transition hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-body text-lg md:text-xl font-semibold text-primary">
                    {section?.title}
                  </h3>
                  <Icon 
                    name={expandedSections.includes(section?.id) ? 'ChevronUp' : 'ChevronDown'} 
                    size={20} 
                    className="text-text-secondary memorial-transition" 
                  />
                </div>
              </button>
              
              {expandedSections.includes(section?.id) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 pb-6"
                >
                  <div className="border-t border-border pt-4">
                    <p className="font-body text-base text-text-primary leading-relaxed whitespace-pre-line">
                      {section?.content}
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Closing Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 border border-primary/20 rounded-lg p-6 md:p-8">
            <p className="font-body text-lg md:text-xl text-text-primary leading-relaxed font-medium">
              {content?.closingStatement}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyWeHoldEventSection;

